import { useRef } from 'react';
import { GETEventResponseBase, GETSalesResponse } from '../../types/TixAPITypes';
import { TixSale } from '../../types/TixTypes';
import { useLoadingStore } from '../../store/useLoadingStore';
import { sleep } from '../scripts';
import { useApiMutation } from '../api/api_v2';
import toast from 'react-hot-toast';

export type EditSaleAPIFunc = (data: {
	originalEvent: GETEventResponseBase;
	saleId: TixSale['id'];
	updatedSale: Partial<TixSale>;
}) => Promise<boolean | undefined>;

export function useEditSaleAPI({
	updateState,
}: {
	updateState: React.Dispatch<React.SetStateAction<GETSalesResponse | undefined>>;
}) {
	const { fetchData: updateSaleAPI } = useApiMutation('tix.put-sale');

	const { removeSaleIdLoading, addSaleIdLoading } = useLoadingStore();

	const currentLoadingIds = useRef<Record<TixSale['id'], boolean>>({});

	const editSaleAPI: EditSaleAPIFunc = async ({
		originalEvent,
		saleId,
		updatedSale,
	}: {
		originalEvent: GETEventResponseBase;
		saleId: TixSale['id'];
		updatedSale: Partial<TixSale>;
	}) => {
		if (currentLoadingIds.current[saleId]) return;
		addSaleIdLoading(saleId);
		currentLoadingIds.current[saleId] = true;

		// NOTE: Make saving feel more realistic
		await sleep(500);

		try {
			// NOTE: Compile the new order from the original order and changes
			const originalSale = originalEvent.sales.find((sale) => sale.id === saleId);
			if (!originalSale) return;

			const fullUpdatedSale: TixSale = {
				...originalSale,
				...updatedSale,
			} as TixSale;

			// NOTE: Send request and handle error
			const res = await updateSaleAPI({
				body: fullUpdatedSale,
			});

			if (!res?.success) {
				console.error(res?.error);
				toast.error(res?.error ?? `Failed to update ${fullUpdatedSale.reference}}`);
				return;
			}

			// NOTE: On success update the original order
			updateSale({ saleEvent: originalEvent, updatedSale: res.data });
			return true;
		} catch (err) {
			console.error(err);
			toast.error(err?.toString() ?? 'Undefined error occured when trying to edit sale');
			return;
		} finally {
			removeSaleIdLoading(saleId);
			currentLoadingIds.current[saleId] = false;
		}
	};

	const updateSale = ({ saleEvent, updatedSale }: { saleEvent: GETEventResponseBase; updatedSale: TixSale }) => {
		updateState((state) => {
			if (!state) return state;

			const eventRef = state.events.find((event) => event.id === saleEvent.id);
			if (!eventRef) throw new Error('Original event not found!');

			const saleAlreadyExists = eventRef.sales.some((sale) => sale.id == updatedSale.id);

			const sales = saleAlreadyExists
				? eventRef.sales.map((sale) => (sale.id === updatedSale.id ? updatedSale : sale))
				: [...eventRef.sales, updatedSale];

			return {
				...state,
				events: state.events.map((event) => (event.id === saleEvent.id ? { ...event, sales } : event)),
			};
		});
	};

	return { editSaleAPI, updateSale };
}
